import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BrandLogo } from "@/components/BrandLogo";
import { SearchPanel } from "@/components/SearchPanel";
import { CampaignBanner } from "@/components/CampaignBanner";
import { SearchResults } from "@/components/SearchResults";
import { useGoogleCse } from "@/hooks/useGoogleCse";
import { CSE_CX, randomSurprise } from "@/constants/brand";

export default function Home() {
    const { status, executeSearch, retry, RESULTS_DIV_ID } =
        useGoogleCse(CSE_CX);
    const [term, setTerm] = useState("");
    const [searched, setSearched] = useState(false);
    const [request, setRequest] = useState(null);
    const resultsRef = useRef(null);

    const doSearch = (raw) => {
        const value = (raw ?? term).trim();
        if (!value) return;
        setTerm(value);
        setSearched(true);
        setRequest({ q: value, nonce: Date.now() });
    };

    const handleSurprise = () => doSearch(randomSurprise());
    const goHome = () => {
        setSearched(false);
        setRequest(null);
        setTerm("");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        if (!request || status !== "ready") return;
        executeSearch(request.q);
        const id = setTimeout(
            () =>
                resultsRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                }),
            80
        );
        return () => clearTimeout(id);
    }, [request, status, executeSearch]);

    if (searched) {
        return (
            <div className="min-h-screen flex flex-col">
                <header
                    className="sticky top-0 z-30 backdrop-blur-md"
                    style={{
                        background: "rgba(255,253,248,0.92)",
                        borderBottom: "1px solid var(--ck-border)",
                    }}
                >
                    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-3 flex items-center gap-4">
                        <BrandLogo
                            size="sm"
                            showTagline={false}
                            onClick={goHome}
                        />
                        <div className="flex-1">
                            <SearchPanel
                                compact
                                value={term}
                                onChange={setTerm}
                                onSubmit={doSearch}
                                onCategory={(q) => doSearch(q)}
                                onSurprise={handleSurprise}
                            />
                        </div>
                    </div>
                </header>

                <main
                    ref={resultsRef}
                    className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-8 flex-1"
                >
                    <SearchResults
                        status={status}
                        resultsDivId={RESULTS_DIV_ID}
                        retry={retry}
                    />
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <div className="mx-auto w-full max-w-6xl px-6 pt-8 flex items-start justify-between">
                <button
                    onClick={() =>
                        document
                            .getElementById("ck-footer")
                            ?.scrollIntoView({ behavior: "smooth" })
                    }
                    data-testid="about-link"
                    className="text-sm font-medium hover:opacity-70 transition-opacity"
                    style={{ color: "var(--ck-charcoal)" }}
                >
                    About
                </button>
                <p
                    className="tracking-label text-[10px] font-semibold text-right leading-relaxed hidden sm:block"
                    style={{ color: "var(--ck-charcoal)", opacity: 0.55 }}
                >
                    Bigger questions
                    <br />
                    Brighter tomorrows
                </p>
            </div>

            <main className="flex-1 mx-auto w-full max-w-2xl px-6 pt-6 sm:pt-10 pb-16 flex flex-col items-center">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <BrandLogo size="lg" />
                </motion.div>

                <motion.div
                    className="w-full mt-8"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
                >
                    <SearchPanel
                        value={term}
                        onChange={setTerm}
                        onSubmit={doSearch}
                        onCategory={(q) => doSearch(q)}
                        onSurprise={handleSurprise}
                    />
                </motion.div>

                <motion.div
                    className="mt-14 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.35 }}
                >
                    <h1 className="font-serif-brand text-4xl sm:text-5xl font-bold">
                        <span style={{ color: "var(--ck-charcoal)" }}>
                            You ask. Caprio{" "}
                        </span>
                        <span style={{ color: "var(--ck-royal)" }}>knows.</span>
                    </h1>
                    <div className="gold-rule mx-auto mt-4" />
                    <p
                        className="tracking-label text-[10px] sm:text-xs mt-4 font-medium"
                        style={{ color: "var(--ck-charcoal)", opacity: 0.6 }}
                    >
                        Ideas · People · Places · A brighter you
                    </p>
                </motion.div>

                {status === "error" && (
                    <p
                        role="alert"
                        className="mt-8 text-sm text-center"
                        style={{ color: "var(--ck-charcoal)" }}
                    >
                        Search is having trouble loading.{" "}
                        <a
                            className="underline font-semibold"
                            style={{ color: "var(--ck-royal)" }}
                            href={`https://cse.google.com/cse?cx=${CSE_CX}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open CaprioKnows on Google
                        </a>
                    </p>
                )}
            </main>

            <CampaignBanner />
            <Footer />
        </div>
    );
}

function Footer() {
    return (
        <footer
            id="ck-footer"
            data-testid="site-footer"
            className="w-full"
            style={{
                background: "var(--ck-ivory)",
                borderTop: "1px solid var(--ck-border)",
            }}
        >
            <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p
                    className="font-serif-brand text-lg"
                    style={{ color: "var(--ck-charcoal)" }}
                >
                    Caprio<span style={{ color: "var(--ck-royal)" }}>Knows</span>
                </p>
                <p className="text-xs text-neutral-500 text-center">
                    Created by Caprio Jackson · Results powered by Google
                    Programmable Search
                </p>
            </div>
        </footer>
    );
}
